import React, { useEffect, useState } from "react";
import incomeReportService, {
  KPIData,
  PaymentMethodData,
  ProjectIncomeData,
  MonthlyTimelineData,
  TopBookingData,
  IncomeDetail,
} from "../services/incomeReportService";
import "../css/IncomeReport.css";

interface Project {
  id: number;
  name: string;
}

const IncomeReport: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("all");
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<{
    title: string;
    incomes: IncomeDetail[];
  }>({ title: "", incomes: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for actual data
  const [projects, setProjects] = useState<Project[]>([]);
  const [kpiData, setKpiData] = useState<KPIData>({
    totalIncome: 0,
    totalBookings: 0,
    averageIncome: 0,
    pendingAmount: 0,
    thisMonthIncome: 0,
  });
  const [paymentMethodData, setPaymentMethodData] = useState<PaymentMethodData[]>([]);
  const [projectIncomeData, setProjectIncomeData] = useState<ProjectIncomeData[]>([]);
  const [monthlyTimeline, setMonthlyTimeline] = useState<MonthlyTimelineData[]>([]);
  const [topBookings, setTopBookings] = useState<TopBookingData[]>([]);

  // Load data
  const loadData = async (isInitialLoad = false) => {
    try {
      setLoading(true);
      setError(null);

      const data = await incomeReportService.getIncomeReportData(
        selectedProject || undefined,
        dateFrom || undefined,
        dateTo || undefined,
        selectedPaymentMethod !== "all" ? selectedPaymentMethod : undefined
      );

      setKpiData(data.kpi);
      setPaymentMethodData(data.paymentMethodData);
      setProjectIncomeData(data.projectIncomeData);
      setMonthlyTimeline(data.monthlyTimeline);
      setTopBookings(data.topBookings);

      // Only update projects list on initial load or when "all" is selected
      if (isInitialLoad || !selectedProject || selectedProject === "all") {
        setProjects(data.projects);
      }
    } catch (err: any) {
      console.error("Error loading income report data:", err);
      setError(err.response?.data?.error || "Failed to load income report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(true);
  }, []);

  useEffect(() => {
    if (selectedProject !== "") {
      loadData(false);
    }
  }, [selectedProject]);

  const handleApplyFilters = () => {
    loadData(false);
  };

  const handleResetFilters = () => {
    setSelectedProject("");
    setDateFrom("");
    setDateTo("");
    setSelectedPaymentMethod("all");
    setTimeout(() => loadData(true), 0);
  };

  const handleChartClick = async (title: string, filters: any) => {
    try {
      setLoading(true);
      const { incomes } = await incomeReportService.getIncomeDetails(
        selectedProject || undefined,
        dateFrom || undefined,
        dateTo || undefined,
        filters.method || undefined,
        filters.bookingId || undefined
      );

      setModalData({ title, incomes });
      setShowModal(true);
    } catch (err) {
      console.error("Error loading income details:", err);
      setError("Failed to load income details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getPaymentMethodColor = (method: string) => {
    const colors: { [key: string]: string } = {
      cash: "#28a745",
      cheque: "#17a2b8",
      neft: "#ffc107",
      imps: "#fd7e14",
      rtgs: "#6610f2",
      upi: "#e83e8c",
      online: "#20c997",
      unknown: "#6c757d",
    };
    return colors[method.toLowerCase()] || "#6c757d";
  };

  // Helper function for SVG pie chart coordinates
  const getCoordinatesForPercent = (percent: number) => {
    const angle = (percent - 0.25) * 2 * Math.PI; // Start at top (12 o'clock)
    const x = Math.cos(angle) * 40 + 100;
    const y = Math.sin(angle) * 40 + 100;
    return [x, y];
  };

  return (
    <div className="income-report-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">Loading...</div>
        </div>
      )}

      <div className="report-header">
        <h1>Income Report</h1>
        <p className="report-description">
          Complete overview of income, payment methods, and revenue analysis
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Filters */}
      <div className="report-filters" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
        <div className="filter-group">
          <label>Project</label>
          <select
            className="filter-select"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Date From</label>
          <input
            type="date"
            className="filter-input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Date To</label>
          <input
            type="date"
            className="filter-input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Payment Method</label>
          <select
            className="filter-select"
            value={selectedPaymentMethod}
            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
          >
            <option value="all">All Methods</option>
            <option value="Receivable">Receivable</option>
            <option value="cheque">Cheque</option>
            <option value="neft">NEFT</option>
            <option value="imps">IMPS</option>
            <option value="rtgs">RTGS</option>
          </select>
        </div>

        <button className="btn-primary" onClick={handleApplyFilters}>
          Apply Filters
        </button>
        <button className="btn-secondary" onClick={handleResetFilters}>
          Reset
        </button>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: "20px", padding: "10px 20px", background: "#f8f9fa", borderRadius: "8px", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "20px", height: "20px", background: "#28a745", borderRadius: "4px" }}></div>
          <span style={{ fontSize: "14px", fontWeight: "500" }}>✓ Received Amount</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "20px", height: "20px", background: "#ff9800", borderRadius: "4px" }}></div>
          <span style={{ fontSize: "14px", fontWeight: "500" }}>⏳ Pending Receivables</span>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Chart 1: Payment Method Distribution - FULL WIDTH */}
        <div className="chart-card chart-card-wide">
          <h3 className="chart-title">Payment Method Distribution</h3>
          <div className="chart-content">
            <div className="bar-chart-horizontal">
              {paymentMethodData.map((method) => {
                const maxAmount = Math.max(...paymentMethodData.map((m) => m.amount));
                const widthPercent = (method.amount / maxAmount) * 100;
                return (
                  <div key={method.method} className="bar-group">
                    <div className="bar-label" style={{ minWidth: "100px" }}>
                      {method.method.toUpperCase()}
                    </div>
                    <div className="bar-container">
                      <div
                        className="bar-segment"
                        style={{
                          width: `${widthPercent}%`,
                          background: getPaymentMethodColor(method.method),
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          paddingRight: "10px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          transition: "opacity 0.2s",
                        }}
                        onClick={() => handleChartClick(`${method.method} Payments`, { method: method.method })}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                        title={`${formatCurrency(method.amount)} (${method.percentage.toFixed(1)}%) - ${method.count} transactions`}
                      >
                      </div>
                      <span style={{ marginLeft: "10px", fontSize: "14px", fontWeight: "500", color: "#333" }}>
                        {formatCurrency(method.amount)} ({method.percentage.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Chart 2: Project-wise Income */}
        <div className="chart-card chart-card-wide">
          <h3 className="chart-title">Project-wise Income Overview</h3>
          <div className="chart-content">
            <div className="bar-chart-horizontal">
              {projectIncomeData.map((project) => {
                const maxAmount = Math.max(...projectIncomeData.map((p) => p.amount));
                const approvedPercent = (project.approved / maxAmount) * 100;
                const pendingPercent = (project.pending / maxAmount) * 100;
                return (
                  <div key={project.projectId} className="bar-group">
                    <div className="bar-label">{project.projectName}</div>
                    <div className="bar-container">
                      <div
                        className="bar-segment"
                        style={{
                          width: `${approvedPercent}%`,
                          background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                          display: "inline-block",
                          height: "100%",
                        }}
                        onClick={() =>
                          handleChartClick(`${project.projectName} Income`, {})
                        }
                        title={`Approved: ${formatCurrency(project.approved)}`}
                      >
                        {project.approved > 0 && <span className="bar-value">{formatCurrency(project.approved)}</span>}
                      </div>
                      {project.pending > 0 && (
                        <div
                          className="bar-segment"
                          style={{
                            width: `${pendingPercent}%`,
                            background: "linear-gradient(135deg, #ff9800 0%, #ffc107 100%)",
                            display: "inline-block",
                            height: "100%",
                          }}
                          title={`Pending: ${formatCurrency(project.pending)}`}
                        >
                          <span className="bar-value">{formatCurrency(project.pending)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Chart 3: Monthly Income Timeline */}
        <div className="chart-card chart-card-wide">
          <h3 className="chart-title">Income Timeline (Last 6 Months)</h3>
          <div className="chart-content">
            <div className="line-chart">
              {monthlyTimeline.map((data, index) => {
                const maxValue = Math.max(...monthlyTimeline.map((d) => d.amount));
                const approvedHeight = maxValue > 0 ? (data.approved / maxValue) * 100 : 0;
                const pendingHeight = maxValue > 0 ? (data.pending / maxValue) * 100 : 0;
                return (
                  <div key={data.month} className="line-bar">
                    <div style={{ display: "flex", flexDirection: "column-reverse", height: "100%", alignItems: "center" }}>
                      {data.approved > 0 && (
                        <div
                          className="line-bar-fill"
                          style={{
                            height: `${approvedHeight}%`,
                            background: "linear-gradient(180deg, #28a745 0%, #20c997 100%)",
                            width: "100%",
                          }}
                          onClick={() => handleChartClick(`${data.month} Income`, {})}
                          title={`Approved: ${formatCurrency(data.approved)}`}
                        >
                          <span className="line-value">{formatCurrency(data.approved)}</span>
                        </div>
                      )}
                      {data.pending > 0 && (
                        <div
                          className="line-bar-fill"
                          style={{
                            height: `${pendingHeight}%`,
                            background: "linear-gradient(180deg, #ff9800 0%, #ffc107 100%)",
                            width: "100%",
                          }}
                          title={`Pending: ${formatCurrency(data.pending)}`}
                        >
                          <span className="line-value">{formatCurrency(data.pending)}</span>
                        </div>
                      )}
                    </div>
                    <div className="line-label">{data.month}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Chart 4: Top Bookings by Income */}
        <div className="chart-card chart-card-wide">
          <h3 className="chart-title">Top 10 Bookings by Income</h3>
          <div className="chart-content">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Wing</th>
                    <th>Unit</th>
                    <th>Booking ID</th>
                    <th>Customer Name</th>
                    <th>Project</th>
                    <th>Total Income</th>
                  </tr>
                </thead>
                <tbody>
                  {topBookings.map((booking, index) => (
                    <tr
                      key={booking.bookingId}
                      onClick={() =>
                        handleChartClick(`Booking #${booking.bookingId} Income`, {
                          bookingId: booking.bookingId,
                        })
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <td>#{index + 1}</td>
                      <td>{booking.wing || '-'}</td>
                      <td>{booking.unitNumber}</td>
                      <td>{booking.bookingId}</td>
                      <td>{booking.customerName}</td>
                      <td>{booking.projectName}</td>
                      <td className="amount-cell">{formatCurrency(booking.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Chart 5: Pending Receivables List */}
        <div className="chart-card chart-card-wide">
          <h3 className="chart-title">Pending Receivables (Top Bookings)</h3>
          <div className="chart-content">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Wing</th>
                    <th>Unit</th>
                    <th>Booking ID</th>
                    <th>Customer Name</th>
                    <th>Project</th>
                    <th>Total Booking</th>
                    <th>Received</th>
                    <th>Pending</th>
                    <th>% Received</th>
                  </tr>
                </thead>
                <tbody>
                  {topBookings
                    .filter((booking) => booking.pendingAmount > 0)
                    .sort((a, b) => b.pendingAmount - a.pendingAmount)
                    .slice(0, 20)
                    .map((booking, index) => (
                      <tr
                        key={booking.bookingId}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          handleChartClick(`Booking #${booking.bookingId} Income`, {
                            bookingId: booking.bookingId,
                          })
                        }
                      >
                        <td>#{index + 1}</td>
                        <td>{booking.wing || '-'}</td>
                        <td>{booking.unitNumber}</td>
                        <td>{booking.bookingId}</td>
                        <td>{booking.customerName}</td>
                        <td>{booking.projectName}</td>
                        <td className="amount-cell">{formatCurrency(booking.totalAmount)}</td>
                        <td className="amount-cell" style={{ color: "#28a745", fontWeight: "600" }}>
                          {formatCurrency(booking.receivedAmount)}
                        </td>
                        <td className="amount-cell" style={{ color: "#ff9800", fontWeight: "600" }}>
                          {formatCurrency(booking.pendingAmount)}
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ flex: 1, background: "#e0e0e0", height: "8px", borderRadius: "4px", overflow: "hidden" }}>
                              <div
                                style={{
                                  width: `${(booking.receivedAmount / booking.totalAmount) * 100}%`,
                                  height: "100%",
                                  background: "#28a745",
                                }}
                              ></div>
                            </div>
                            <span style={{ fontSize: "12px", fontWeight: "500" }}>
                              {((booking.receivedAmount / booking.totalAmount) * 100).toFixed(1)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {topBookings.filter((b) => b.pendingAmount > 0).length === 0 && (
              <p className="no-data">No pending receivables found</p>
            )}
          </div>
        </div>
      </div>

      {/* Modal for detailed income list */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalData.title}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Booking ID</th>
                      <th>Customer</th>
                      <th>Project</th>
                      <th>Unit</th>
                      <th>Amount</th>
                      <th>Payment Date</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th>Receipt No</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.incomes.map((income) => (
                      <tr key={income.id}>
                        <td>{income.bookingId}</td>
                        <td>{income.customerName}</td>
                        <td>{income.projectName}</td>
                        <td>{income.unitNumber}</td>
                        <td className="amount-cell">{formatCurrency(income.amount)}</td>
                        <td>{formatDate(income.paymentDate)}</td>
                        <td>
                          <span className="badge">{income.paymentMethod.toUpperCase()}</span>
                        </td>
                        <td>{income.referenceNumber}</td>
                        <td>{income.receiptNo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {modalData.incomes.length === 0 && (
                <p className="no-data">No income records found</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeReport;
